import { format, parseISO } from 'date-fns';

/**
 * Convert a date/time from one timezone to another using Intl API
 * @param date - The date to convert
 * @param fromTimezone - Source timezone (e.g., 'UTC', 'America/New_York')
 * @param toTimezone - Target timezone
 * @returns The converted date
 */
export function convertTimezone(date: Date | string, fromTimezone: string, toTimezone: string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  // If timezones are the same, return as is
  if (fromTimezone === toTimezone) {
    return dateObj;
  }
  
  // Use Intl.DateTimeFormat to get the date in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: toTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(dateObj);
  const getValue = (type: string) => parts.find(p => p.type === type)?.value || '0';
  
  const year = parseInt(getValue('year'));
  const month = parseInt(getValue('month')) - 1; // Month is 0-indexed
  const day = parseInt(getValue('day'));
  const hour = parseInt(getValue('hour'));
  const minute = parseInt(getValue('minute'));
  const second = parseInt(getValue('second'));
  
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Format a date in a specific timezone
 * @param date - The date to format
 * @param timezone - The timezone to use
 * @param formatStr - The format string (default: 'h:mm a')
 * @returns Formatted date string
 */
export function formatInTimezoneCustom(
  date: Date | string,
  timezone: string,
  formatStr: string = 'h:mm a'
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  try {
    // Convert to target timezone first
    const convertedDate = convertTimezone(dateObj, 'UTC', timezone);
    return format(convertedDate, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return format(dateObj, formatStr);
  }
}

/**
 * Get the current time in a specific timezone
 * @param timezone - The timezone
 * @returns Current date in the specified timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  return convertTimezone(new Date(), 'UTC', timezone);
}

/**
 * Calculate the offset between two timezones in minutes
 * @param timezone1 - First timezone
 * @param timezone2 - Second timezone
 * @returns Offset in minutes
 */
export function getTimezoneOffset(timezone1: string, timezone2: string): number {
  const now = new Date();
  const date1 = convertTimezone(now, 'UTC', timezone1);
  const date2 = convertTimezone(now, 'UTC', timezone2);
  
  return (date2.getTime() - date1.getTime()) / (1000 * 60);
}

/**
 * Format timezone offset for display (e.g., "UTC+5:30")
 * @param timezone - The timezone to format
 * @returns Formatted offset string
 */
export function formatTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';
    return timeZoneName;
  } catch (error) {
    return 'UTC';
  }
}

/**
 * Convert booking times to a different timezone
 * @param booking - The booking object
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Booking with converted times
 */
export function convertBookingTimezone<T extends { startTime: string; endTime: string }>(
  booking: T,
  fromTimezone: string,
  toTimezone: string
): T {
  if (fromTimezone === toTimezone) {
    return booking;
  }
  
  const startDate = parseISO(booking.startTime);
  const endDate = parseISO(booking.endTime);
  
  const convertedStart = convertTimezone(startDate, fromTimezone, toTimezone);
  const convertedEnd = convertTimezone(endDate, fromTimezone, toTimezone);
  
  return {
    ...booking,
    startTime: convertedStart.toISOString(),
    endTime: convertedEnd.toISOString(),
  };
}

/**
 * Get timezone display name
 * @param timezone - The timezone value
 * @returns Display name with offset
 */
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const timezoneName = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return `${timezone} (${timezoneName})`;
  } catch (error) {
    return timezone;
  }
}

/**
 * Check if daylight saving time is active in a timezone
 * @param timezone - The timezone to check
 * @param date - The date to check (defaults to now)
 * @returns True if DST is active
 */
export function isDSTActive(timezone: string, date: Date = new Date()): boolean {
  try {
    const january = new Date(date.getFullYear(), 0, 1);
    const july = new Date(date.getFullYear(), 6, 1);
    
    const getOffset = (d: Date) => {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(d);
      return parts.find(p => p.type === 'timeZoneName')?.value || '';
    };
    
    const janOffset = getOffset(january);
    const julyOffset = getOffset(july);
    const currentOffset = getOffset(date);
    
    // If offsets differ between winter and summer, DST is used
    if (janOffset !== julyOffset) {
      // Check if current offset matches summer offset
      return currentOffset === julyOffset;
    }
    
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Adjust time slots for timezone display
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Adjusted hour and minute
 */
export function adjustTimeSlotForTimezone(
  hour: number,
  minute: number,
  fromTimezone: string,
  toTimezone: string
): { hour: number; minute: number } {
  if (fromTimezone === toTimezone) {
    return { hour, minute };
  }
  
  // Create a date with the given time
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  
  // Convert to target timezone
  const converted = convertTimezone(date, fromTimezone, toTimezone);
  
  return {
    hour: converted.getHours(),
    minute: converted.getMinutes(),
  };
}
